<template>
  <!-- Where a ticket is in its lifecycle: Submitted › Assigned › In progress ›
       Resolved › Closed. The steps behind it are ticked, the one it is on is
       lit in its own colour with when it got there, and — for staff — the
       Assigned step names who looks after it. On a phone the steps are dots,
       with the one it is on spelled out under them. The same component lives
       in the admin and in both organization apps. -->
  <div class="tl" :class="`tl--${stage}`">
    <ol class="tl__steps" :aria-label="`Status: ${currentLabel}`">
      <li
        v-for="(step, index) in steps"
        :key="step.key"
        class="tl-step"
        :class="{ 'is-done': index < current, 'is-current': index === current }"
        :aria-current="index === current ? 'step' : null"
      >
        <span class="tl-step__dot" aria-hidden="true">
          <i class="pi" :class="index < current ? 'pi-check' : step.icon"></i>
        </span>
        <span class="tl-step__text">
          <span class="tl-step__label">{{ step.label }}</span>
          <span v-if="detailOf(step, index)" class="tl-step__detail" :title="detailOf(step, index)">{{ detailOf(step, index) }}</span>
        </span>
      </li>
    </ol>
    <p class="tl__caption" aria-hidden="true">
      <strong>{{ currentLabel }}</strong>
      <template v-if="captionDetail"> · {{ captionDetail }}</template>
    </p>
  </div>
</template>
<script>
import { messageTimeLabel } from '@/utils/ticket';
import { TICKET_STAGE_ASSIGNED, TICKET_STAGES, ticketStageIndex, ticketStageLabel } from '@/utils/ticket_lifecycle';

export default {
  name: 'TicketLifecycle',
  props: {
    // submitted, assigned, in_progress, resolved or closed.
    stage: {
      type: String,
      required: true,
    },
    // When the ticket got to the stage it is on.
    stageChangedAt: {
      type: [String, Date],
      default: null,
    },
    // Who looks after it — staff see it; an organization is not told.
    assigneeName: {
      type: String,
      default: '',
    },
  },
  computed: {
    steps() {
      return TICKET_STAGES;
    },
    current() {
      return Math.max(ticketStageIndex(this.stage), 0);
    },
    currentLabel() {
      return ticketStageLabel(this.stage);
    },
    since() {
      return messageTimeLabel(this.stageChangedAt);
    },
    captionDetail() {
      const parts = [];
      if (this.assigneeName && this.current >= 1) parts.push(this.assigneeName);
      if (this.since) parts.push(`since ${this.since}`);
      return parts.join(' · ');
    },
  },
  methods: {
    detailOf(step, index) {
      const parts = [];
      if (step.key === TICKET_STAGE_ASSIGNED && this.assigneeName && index <= this.current) parts.push(this.assigneeName);
      if (index === this.current && this.since) parts.push(this.since);
      return parts.join(' · ');
    },
  },
};
</script>
<style scoped>
/* Each stage's own colour — the same the status pills wear. */
.tl {
  --tl-done: var(--color-primary, #4B3FE5);
  --tl-done-soft: var(--color-primary-soft, #EEEDFD);
  --tl-tone: #2563EB;
  --tl-tint: #EAF1FF;
}

.tl--assigned { --tl-tone: var(--color-primary, #4B3FE5); --tl-tint: var(--color-primary-soft, #EEEDFD); }
.tl--in_progress { --tl-tone: #C2680B; --tl-tint: #FFF4E5; }
.tl--resolved { --tl-tone: #0E9F6E; --tl-tint: #E8F8F0; }
.tl--closed { --tl-tone: #6B7280; --tl-tint: #F1F2F5; }

.tl__steps {
  display: flex;
  align-items: flex-start;
  margin: 0;
  padding: 0;
  list-style: none;
}

.tl-step {
  display: flex;
  flex: 1 1 0;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}

/* The line to the next step: lit once that step is reached. */
.tl-step:not(:last-child)::after {
  content: '';
  flex: 1 1 auto;
  min-width: 12px;
  height: 2px;
  margin: 11px 10px 0 2px;
  border-radius: 2px;
  background: var(--color-border, #E4E4E7);
}

.tl-step.is-done:not(:last-child)::after {
  background: var(--tl-done);
}

.tl-step:last-child {
  flex: 0 0 auto;
}

.tl-step__dot {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1.5px solid var(--color-border, #E4E4E7);
  border-radius: 50%;
  background: var(--color-bg-canvas, #FFFFFF);
  color: var(--color-text-muted, #A1A1AA);
  font-size: 0.66rem;
  transition: background-color .2s ease, border-color .2s ease, color .2s ease;
}

.tl-step.is-done .tl-step__dot {
  border-color: transparent;
  background: var(--tl-done-soft);
  color: var(--tl-done);
  font-weight: 700;
}

.tl-step.is-current .tl-step__dot {
  border-color: transparent;
  background: var(--tl-tone);
  color: #FFFFFF;
  box-shadow: 0 0 0 4px var(--tl-tint);
}

.tl-step__text {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  padding-top: 3px;
}

.tl-step__label {
  color: var(--color-text-muted, #71717A);
  font-size: 0.78rem;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
}

.tl-step.is-done .tl-step__label {
  color: var(--color-text-secondary, #52525B);
}

.tl-step.is-current .tl-step__label {
  color: var(--tl-tone);
  font-weight: 600;
}

.tl-step__detail {
  overflow: hidden;
  max-width: 150px;
  color: var(--color-text-muted, #71717A);
  font-size: 0.7rem;
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tl__caption {
  display: none;
  margin: 8px 0 0;
  color: var(--color-text-muted, #71717A);
  font-size: 0.75rem;
}

.tl__caption strong {
  color: var(--tl-tone);
  font-weight: 600;
}

/* Narrow: the labels give way to the caption. */
@media (max-width: 767.98px) {
  .tl-step__detail {
    display: none;
  }
}

@media (max-width: 575.98px) {
  .tl-step__text {
    display: none;
  }

  .tl-step:not(:last-child)::after {
    margin: 11px 4px 0;
  }

  .tl__caption {
    display: block;
  }
}
</style>
